<section class="py-24 bg-slate-50 dark:bg-slate-950 border-b border-slate-100 dark:border-slate-800 transition-colors duration-300" id="solutions">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <!-- Section Header -->
    <div class="text-center max-w-3xl mx-auto mb-12 observe-viewport effect-fade-up">
      <div class="inline-flex items-center gap-2 border rounded-full px-4 py-1.5 mb-6 bg-white dark:bg-slate-800 border-slate-200/60 dark:border-slate-700">
        <span class="text-xs font-semibold text-indigo-600 dark:text-indigo-400">Full Suite</span>
        <span class="text-sm text-slate-500 dark:text-slate-400">Lending Solutions</span>
      </div>
      <h2 class="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 dark:text-white mb-4">
        Our Credit Solutions
      </h2>
      <p class="text-lg text-slate-500 dark:text-slate-400">
        Access diverse credit solutions designed to help you lead the conversation, guide financial outcomes, and protect long-term wealth strategies.
      </p>
    </div>

    <!-- Solution Navigation Tabs -->
    <div class="mb-10 observe-viewport effect-fade-up delay-100">
      <div class="flex flex-wrap justify-center gap-2 md:gap-3">
        <!-- Overview Tab -->
        <button
          (click)="setActiveSolution('overview')"
          [ngClass]="activeSolutionId === 'overview' 
            ? 'px-4 md:px-5 py-2.5 rounded-full text-sm font-medium text-white bg-indigo-600 shadow-md shadow-indigo-200 dark:shadow-indigo-900/50 transition-all' 
            : 'px-4 md:px-5 py-2.5 rounded-full text-sm font-medium text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-slate-300 dark:hover:border-slate-600 transition-all'">
          <span class="flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
            </svg>
            Overview
          </span>
        </button>

        <!-- Solution Tabs -->
        @for (solution of solutions; track solution) {
          <button
            (click)="setActiveSolution(solution.id)"
          [ngClass]="activeSolutionId === solution.id 
            ? 'px-4 md:px-5 py-2.5 rounded-full text-sm font-medium text-white bg-indigo-600 shadow-md shadow-indigo-200 dark:shadow-indigo-900/50 transition-all' 
            : 'px-4 md:px-5 py-2.5 rounded-full text-sm font-medium text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-slate-300 dark:hover:border-slate-600 transition-all'">
            <span class="flex items-center gap-2">
              <!-- City Icon -->
              @if (solution.icon === 'city') {
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15 11V5l-3-3-3 3v2H3v14h18V11h-6zm-8 8H5v-2h2v2zm0-4H5v-2h2v2zm0-4H5V9h2v2zm6 8h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V9h2v2zm0-4h-2V5h2v2zm6 12h-2v-2h2v2zm0-4h-2v-2h2v2z"/>
                </svg>
              }
              <!-- Case Icon -->
              @if (solution.icon === 'case') {
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 7h-4V5c0-1.1-.9-2-2-2h-4c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2zM10 5h4v2h-4V5zm10 14H4V9h16v10z"/>
                </svg>
              }
              <!-- Home Icon -->
              @if (solution.icon === 'home') {
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
                </svg>
              }
              <!-- Chart Icon -->
              @if (solution.icon === 'chart') {
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z"/>
                </svg>
              }
              <!-- Shield Icon -->
              @if (solution.icon === 'shield') {
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
                </svg>
              }
              <!-- Gem Icon -->
              @if (solution.icon === 'gem') {
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 3H5L2 9l10 12L22 9l-3-6zm-9 6h4l-2 6-2-6zm3-2h-2l-.5-2h3l-.5 2zm3.5 0L16 5.5l1.5 1.5h-1.5zm-8.5 0L7 5.5 8.5 7H7zm3.15 10.35L6.5 9h3l2.35 8.35zm4.7 0L14.5 9h3l-3.65 8.35z"/>
                </svg>
              }
              <!-- Trending Up Icon -->
              @if (solution.icon === 'trending-up') {
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/>
                </svg>
              }
              <span class="hidden sm:inline">{{ solution.title.split(' ').slice(0, 2).join(' ') }}</span>
              <span class="sm:hidden">{{ solution.title.split(' ')[0] }}</span>
            </span>
          </button>
        }
      </div>
    </div>

    <!-- Overview Content -->
    @if (activeSolutionId === 'overview') {
      <div class="animate-fade-in">
        <div class="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-xl overflow-hidden">
          <!-- Overview Header -->
          <div class="bg-gradient-to-r from-indigo-600 to-indigo-700 px-8 py-10 md:px-12 md:py-14">
            <div class="flex items-center gap-4 mb-4">
              <div class="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="white">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <h3 class="text-2xl md:text-3xl font-semibold text-white">{{ overviewContent.title }}</h3>
            </div>
            <p class="text-indigo-100 text-lg max-w-4xl">
              A comprehensive lending platform built for financial advisors to deliver tailored credit solutions across every asset class.
            </p>
          </div>
          <!-- Overview Body -->
          <div class="p-8 md:p-12">
            <div class="prose prose-slate dark:prose-invert max-w-none">
              @for (paragraph of overviewContent.paragraphs; track paragraph; let i = $index) {
                <p
                  class="text-slate-600 dark:text-slate-400 leading-relaxed mb-6 last:mb-0">
                  {{ paragraph }}
                </p>
              }
            </div>
            <!-- Solution Quick Links -->
            <div class="mt-12 pt-8 border-t border-slate-100 dark:border-slate-800">
              <h4 class="text-lg font-semibold text-slate-900 dark:text-white mb-6">Explore Our Solutions</h4>
              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                @for (solution of solutions; track solution) {
                  <button
                    (click)="setActiveSolution(solution.id)"
                    class="flex items-start gap-4 p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 hover:bg-white dark:hover:bg-slate-800 hover:border-indigo-200 dark:hover:border-indigo-600 hover:shadow-md transition-all text-left group">
                    <div class="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shrink-0 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                      <!-- City Icon -->
                      @if (solution.icon === 'city') {
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M15 11V5l-3-3-3 3v2H3v14h18V11h-6zm-8 8H5v-2h2v2zm0-4H5v-2h2v2zm0-4H5V9h2v2zm6 8h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V9h2v2zm0-4h-2V5h2v2zm6 12h-2v-2h2v2zm0-4h-2v-2h2v2z"/>
                        </svg>
                      }
                      <!-- Case Icon -->
                      @if (solution.icon === 'case') {
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20 7h-4V5c0-1.1-.9-2-2-2h-4c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2zM10 5h4v2h-4V5zm10 14H4V9h16v10z"/>
                        </svg>
                      }
                      <!-- Home Icon -->
                      @if (solution.icon === 'home') {
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
                        </svg>
                      }
                      <!-- Chart Icon -->
                      @if (solution.icon === 'chart') {
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z"/>
                        </svg>
                      }
                      <!-- Shield Icon -->
                      @if (solution.icon === 'shield') {
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
                        </svg>
                      }
                      <!-- Gem Icon -->
                      @if (solution.icon === 'gem') {
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 3H5L2 9l10 12L22 9l-3-6zm-9 6h4l-2 6-2-6zm3-2h-2l-.5-2h3l-.5 2zm3.5 0L16 5.5l1.5 1.5h-1.5zm-8.5 0L7 5.5 8.5 7H7zm3.15 10.35L6.5 9h3l2.35 8.35zm4.7 0L14.5 9h3l-3.65 8.35z"/>
                        </svg>
                      }
                      <!-- Trending Up Icon -->
                      @if (solution.icon === 'trending-up') {
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/>
                        </svg>
                      }
                    </div>
                    <div class="flex-1 min-w-0">
                      <h5 class="font-semibold text-slate-900 dark:text-white text-sm mb-1 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">{{ solution.title }}</h5>
                      <p class="text-xs text-slate-500 dark:text-slate-400 line-clamp-2">{{ solution.shortDescription }}</p>
                    </div>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-slate-300 dark:text-slate-600 shrink-0 mt-1 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 group-hover:translate-x-1 transition-all">
                      <path d="M5 12h14"/>
                      <path d="m12 5 7 7-7 7"/>
                    </svg>
                  </button>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    }

    <!-- Solution Detail Content -->
    @if (activeSolutionId !== 'overview' && currentSolution) {
      <div class="animate-fade-in">
        <div class="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-xl overflow-hidden">
          <!-- Solution Header -->
          <div class="bg-gradient-to-r from-indigo-600 to-indigo-700 px-8 py-10 md:px-12 md:py-14">
            <div class="flex items-center gap-4 mb-4">
              <div class="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <!-- City Icon -->
                @if (currentSolution.icon === 'city') {
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="white">
                    <path d="M15 11V5l-3-3-3 3v2H3v14h18V11h-6zm-8 8H5v-2h2v2zm0-4H5v-2h2v2zm0-4H5V9h2v2zm6 8h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V9h2v2zm0-4h-2V5h2v2zm6 12h-2v-2h2v2zm0-4h-2v-2h2v2z"/>
                  </svg>
                }
                <!-- Case Icon -->
                @if (currentSolution.icon === 'case') {
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="white">
                    <path d="M20 7h-4V5c0-1.1-.9-2-2-2h-4c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2zM10 5h4v2h-4V5zm10 14H4V9h16v10z"/>
                  </svg>
                }
                <!-- Home Icon -->
                @if (currentSolution.icon === 'home') {
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="white">
                    <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
                  </svg>
                }
                <!-- Chart Icon -->
                @if (currentSolution.icon === 'chart') {
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="white">
                    <path d="M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z"/>
                  </svg>
                }
                <!-- Shield Icon -->
                @if (currentSolution.icon === 'shield') {
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="white">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
                  </svg>
                }
                <!-- Gem Icon -->
                @if (currentSolution.icon === 'gem') {
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="white">
                    <path d="M19 3H5L2 9l10 12L22 9l-3-6zm-9 6h4l-2 6-2-6zm3-2h-2l-.5-2h3l-.5 2zm3.5 0L16 5.5l1.5 1.5h-1.5zm-8.5 0L7 5.5 8.5 7H7zm3.15 10.35L6.5 9h3l2.35 8.35zm4.7 0L14.5 9h3l-3.65 8.35z"/>
                  </svg>
                }
                <!-- Trending Up Icon -->
                @if (currentSolution.icon === 'trending-up') {
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="white">
                    <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/>
                  </svg>
                }
              </div>
              <div>
                <h3 class="text-2xl md:text-3xl font-semibold text-white">{{ currentSolution.title }}</h3>
                <p class="text-indigo-200 mt-1">{{ currentSolution.shortDescription }}</p>
              </div>
            </div>
          </div>
          <!-- Solution Body -->
          <div class="p-8 md:p-12">
            <!-- Description -->
            <div class="mb-10">
              <h4 class="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-indigo-600 dark:text-indigo-400">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10 9 9 9 8 9"/>
                </svg>
                Description
              </h4>
              <div class="space-y-4">
                @for (paragraph of currentSolution.description; track paragraph) {
                  <p class="text-slate-600 dark:text-slate-400 leading-relaxed">
                    {{ paragraph }}
                  </p>
                }
              </div>
            </div>
            <!-- Key Features Accordion -->
            <div class="mb-10">
              <div class="border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50/50 dark:bg-slate-800/50 overflow-hidden hover:border-indigo-200 dark:hover:border-indigo-600 transition-colors">
                <details class="group [&_summary::-webkit-details-marker]:hidden">
                  <summary class="flex justify-between items-center p-5 cursor-pointer list-none">
                    <div class="flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-indigo-600 dark:text-indigo-400">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                      </svg>
                      <span class="text-lg font-semibold text-slate-900 dark:text-white">Key Features</span>
                    </div>
                    <span class="transition-transform group-open:rotate-180 shrink-0 ml-4">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-slate-400 dark:text-slate-500">
                        <path d="m6 9 6 6 6-6"/>
                      </svg>
                    </span>
                  </summary>
                  <div class="px-5 pb-5 pt-0">
                    <div class="space-y-4">
                      @for (feature of currentSolution.keyFeatures; track feature; let i = $index) {
                        <div class="flex items-start gap-3">
                          <div class="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400 text-sm font-semibold shrink-0">
                            {{ i + 1 }}
                          </div>
                          <div class="flex-1">
                            <h5 class="font-semibold text-slate-900 dark:text-white mb-1">{{ feature.title }}</h5>
                            <p class="text-slate-600 dark:text-slate-400 leading-relaxed">{{ feature.content }}</p>
                          </div>
                        </div>
                      }
                    </div>
                    <!-- Disclaimer inside dropdown -->
                    @if (currentSolution.disclaimer) {
                      <div class="mt-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
                        <div class="flex gap-3">
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5">
                            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                            <line x1="12" y1="9" x2="12" y2="13"/>
                            <line x1="12" y1="17" x2="12.01" y2="17"/>
                          </svg>
                          <div>
                            <h5 class="font-semibold text-amber-800 dark:text-amber-400 mb-1 text-sm">Disclaimer</h5>
                            <p class="text-sm text-amber-700 dark:text-amber-300">{{ currentSolution.disclaimer }}</p>
                          </div>
                        </div>
                      </div>
                    }
                  </div>
                </details>
              </div>
            </div>
            <!-- Navigation Footer -->
            <div class="mt-10 pt-8 border-t border-slate-100 dark:border-slate-800">
              <div class="flex flex-wrap items-center justify-between gap-4">
                <button
                  (click)="setActiveSolution('overview')"
                  class="inline-flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="m12 19-7-7 7-7"/>
                    <path d="M19 12H5"/>
                  </svg>
                  Back to Overview
                </button>
                <div class="flex flex-wrap gap-2">
                  <span class="text-sm text-slate-500 dark:text-slate-400">Explore:</span>
                  @for (s of solutions; track s) {
                    <button
                      [hidden]="s.id === currentSolution.id"
                      (click)="setActiveSolution(s.id)"
                      class="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors">
                      {{ s.title.split(' ').slice(0, 2).join(' ') }}
                    </button>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    }

    <!-- Download White Paper Button -->
    <div class="text-center mt-12 observe-viewport effect-fade-up">
      <a href="/documents/white-paper" target="_blank" rel="noopener noreferrer"
        class="group inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-indigo-700 text-sm font-semibold text-white shadow-lg shadow-indigo-500/30 dark:shadow-indigo-500/20 hover:from-indigo-700 hover:to-indigo-800 hover:shadow-xl hover:shadow-indigo-500/40 hover:-translate-y-0.5 transition-all duration-300">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
          <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
        </svg>
        View White Paper
      </a>
    </div>
  </div>
</section>
